<script lang="ts">
  import { formatCount, groupDigits, TIMEOUT_CEILING_MS } from '../../lib/complexity';
  import {
    chartSummary,
    dodgeLabels,
    splitSegments,
    unionNs,
    yDomainMax,
    type ChartKind,
    type ChartSeries,
  } from './chart-model';
  import { powToSpeech, splitPow } from './notation';
  import Pow from './Pow.svelte';
  import {
    chartLayout,
    crossPath,
    formatAxisN,
    linearScale,
    linePath,
    nearestIndex,
    niceTicks,
    tooltipLeft,
  } from './scale';

  /**
   * 計測結果の折れ線（components.md §3.3 DurationChart、dataviz 規約）。
   * x は線形（N）。viewBox で拡大縮小せず、コンテナ幅を測って実寸の座標で描く。
   */
  interface Props {
    kind: ChartKind;
    title: string;
    series: ChartSeries[];
    /** x 軸の右端の N（同じ画面の 2 つのグラフで揃える）。 */
    xMax: number;
    /** 計測中は前回の描画を 0.5 不透明で残す。 */
    dimmed?: boolean;
  }

  let { kind, title, series, xMax, dimmed = false }: Props = $props();
  const uid = $props.id();

  let wrapWidth = $state(640);
  let wrap: HTMLDivElement | undefined = $state();
  let tip: HTMLDivElement | undefined = $state();
  let tipWidth = $state(176);
  let idx = $state(-1);
  let tipOpen = $state(false);

  $effect(() => {
    const el = wrap;
    if (!el) return;
    wrapWidth = el.clientWidth;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      wrapWidth = el.clientWidth;
    });
    ro.observe(el);
    return () => ro.disconnect();
  });

  $effect(() => {
    if (tipOpen && tip) tipWidth = tip.offsetWidth;
  });

  const L = $derived(chartLayout(wrapWidth));
  const xTicks = $derived(niceTicks(xMax, L.narrow ? 2 : 4));
  const xTop = $derived(xTicks[xTicks.length - 1] ?? 1);
  const yTicks = $derived(
    kind === 'duration' ? [0, 1000, 2000, 3000] : niceTicks(yDomainMax(kind, series), 4),
  );
  const yTop = $derived(
    kind === 'duration' ? yDomainMax(kind, series) : (yTicks[yTicks.length - 1] ?? 1),
  );
  const x = $derived(linearScale(0, xTop, L.margin.l, L.width - L.margin.r));
  const y = $derived(linearScale(0, yTop, L.height - L.margin.b, L.margin.t));
  /** 打ち切りの ✕ を置く高さ。実行時間は制限線と上端の間（天井）。 */
  const ceilY = $derived((y(TIMEOUT_CEILING_MS) + L.margin.t) / 2);

  function py(kindIsCut: boolean, value: number): number {
    return kind === 'duration' && kindIsCut ? ceilY : y(value);
  }

  const drawn = $derived(
    series.map((s) => {
      const seg = splitSegments(s.points);
      return {
        s,
        solid: linePath(seg.solid.map((p) => ({ x: x(p.n), y: py(false, p.value) }))),
        dashed:
          kind === 'steps'
            ? linePath(seg.dashed.map((p) => ({ x: x(p.n), y: py(p.cut, p.value) })))
            : '',
        dots: s.points.map((p) => ({ p, cx: x(p.n), cy: py(p.cut, p.value) })),
      };
    }),
  );

  const endLabels = $derived.by(() => {
    const ends = drawn
      .map((d) => {
        const last = d.dots[d.dots.length - 1];
        return last ? { key: d.s.key, label: d.s.label, x: last.cx + 10, y: last.cy + 4 } : null;
      })
      .filter((e) => e !== null);
    const ys = dodgeLabels(
      ends.map((e) => e.y),
      14,
    );
    return ends.map((e, i) => ({ ...e, y: ys[i] ?? e.y }));
  });

  const ns = $derived(unionNs(series));
  const summary = $derived(chartSummary(kind, series));
  const current = $derived(idx >= 0 ? ns[idx] : undefined);

  function yLabel(v: number): string {
    if (v === 0) return '0';
    return kind === 'duration' ? groupDigits(v) : formatCount(v);
  }

  function show(i: number) {
    if (ns.length === 0) return;
    idx = Math.max(0, Math.min(ns.length - 1, i));
    tipOpen = true;
  }

  function hide() {
    tipOpen = false;
  }

  function onpointermove(e: PointerEvent) {
    const el = e.currentTarget;
    if (!(el instanceof SVGSVGElement)) return;
    const r = el.getBoundingClientRect();
    show(
      nearestIndex(
        ns.map((n) => x(n)),
        e.clientX - r.left,
      ),
    );
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      hide();
      return;
    }
    const base = idx < 0 ? 0 : idx;
    const target: Record<string, number> = {
      ArrowRight: base + 1,
      ArrowLeft: base - 1,
      Home: 0,
      End: ns.length - 1,
    };
    const next = target[e.key];
    if (next === undefined) return;
    e.preventDefault();
    show(next);
  }

  function rowFor(s: ChartSeries, n: number): string {
    const p = s.points.find((q) => q.n === n);
    return p ? p.text : 'なし';
  }

  const tipText = $derived(
    current === undefined
      ? ''
      : `N = ${groupDigits(current)}。` +
          series.map((s) => `${s.label} ${rowFor(s, current)}`).join('、'),
  );
</script>

<figure class="chart" class:dimmed>
  <figcaption id="{uid}-title" class="chart-title">{title}</figcaption>
  {#if series.length >= 2}
    <ul class="legend" aria-label="凡例">
      {#each series as s (s.key)}
        <li><span class="key k{s.key}" aria-hidden="true"></span>{s.label}</li>
      {/each}
    </ul>
  {/if}
  <div class="chart-wrap" bind:this={wrap}>
    <!-- グラフ全体を 1 つのフォーカス対象にし、←→ で点を移動する（components.md §3.3 A11y） -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
    <svg
      width={L.width}
      height={L.height}
      viewBox="0 0 {L.width} {L.height}"
      role="img"
      tabindex="0"
      aria-labelledby="{uid}-title"
      aria-describedby="{uid}-desc"
      data-testid="meter-chart-{kind}"
      {onpointermove}
      onpointerleave={hide}
      onfocus={() => show(idx < 0 ? 0 : idx)}
      onblur={hide}
      {onkeydown}
    >
      <desc id="{uid}-desc">{summary}</desc>
      <g class="axis">
        {#each yTicks as v (v)}
          <line class="gridline" x1={L.margin.l} x2={L.width - L.margin.r} y1={y(v)} y2={y(v)} />
          <text x={L.margin.l - 8} y={y(v) + 4} text-anchor="end"
            >{#each splitPow(yLabel(v)) as part, i (i)}{#if part.sup}<tspan class="sup" dy="-0.45em"
                  >{part.text}</tspan
                >{:else}<tspan>{part.text}</tspan>{/if}{/each}</text
          >
        {/each}
        {#each xTicks as n (n)}
          <text x={x(n)} y={L.height - L.margin.b + 18} text-anchor="middle">{formatAxisN(n)}</text>
        {/each}
        <text x={L.width - L.margin.r} y={L.height - 4} text-anchor="end">N</text>
        <text x={L.margin.l - 8} y={L.margin.t - 10} text-anchor="end"
          >{kind === 'duration' ? 'ms' : '回'}</text
        >
      </g>

      {#if kind === 'duration'}
        <line
          class="limit"
          x1={L.margin.l}
          x2={L.width - L.margin.r}
          y1={y(TIMEOUT_CEILING_MS)}
          y2={y(TIMEOUT_CEILING_MS)}
        />
        <text class="label" x={L.margin.l + 6} y={y(TIMEOUT_CEILING_MS) + 16}
          >{L.narrow ? '制限' : '制限 3 秒'}</text
        >
      {/if}

      {#each drawn as d (d.s.key)}
        <path d={d.solid} class="line s{d.s.key}" />
        {#if d.dashed}
          <path d={d.dashed} class="line cutline s{d.s.key}" />
        {/if}
        {#each d.dots as dot (dot.p.n)}
          {#if dot.p.cut}
            <path d={crossPath(dot.cx, dot.cy)} class="cross-mark s{d.s.key}" />
          {:else}
            <circle cx={dot.cx} cy={dot.cy} r="4" class="dot f{d.s.key}" />
          {/if}
        {/each}
        {#each d.dots.filter((q) => q.p.cut).slice(0, 1) as dot (dot.p.n)}
          <text class="dlabel" x={dot.cx} y={dot.cy - 10} text-anchor="middle"
            >{kind === 'duration' ? 'TLE' : dot.p.status === 're' ? 'RE' : '打ち切り'}</text
          >
        {/each}
      {/each}

      {#each endLabels as e (e.key)}
        {#if e.x < L.width - 4}
          <text class="dlabel" x={Math.min(e.x, L.width - L.margin.r + 6)} y={e.y}>{e.label}</text>
        {/if}
      {/each}

      {#if tipOpen && current !== undefined}
        <line
          class="crosshair"
          x1={x(current)}
          x2={x(current)}
          y1={L.margin.t}
          y2={L.height - L.margin.b}
        />
      {/if}
      <rect
        class="hit"
        x={L.margin.l - 12}
        y={L.margin.t}
        width={L.width - L.margin.l - L.margin.r + 24}
        height={L.height - L.margin.t - L.margin.b}
      />
    </svg>
    {#if tipOpen && current !== undefined}
      <div
        class="tooltip"
        bind:this={tip}
        style:left="{tooltipLeft(x(current), tipWidth, L.width)}px"
        style:top="{L.margin.t}px"
        aria-hidden="true"
      >
        <div class="t-head num">N = {groupDigits(current)}</div>
        {#each series as s (s.key)}
          <div class="t-row">
            <span class="key k{s.key}"></span><b><Pow text={rowFor(s, current)} /></b><span
              class="text-soft">{s.label}</span
            >
          </div>
        {/each}
      </div>
    {/if}
    <p class="visually-hidden" aria-live="polite">{tipOpen ? powToSpeech(tipText) : ''}</p>
  </div>
</figure>

<style>
  .chart {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    transition: opacity var(--dur-base);
  }
  .chart.dimmed {
    opacity: 0.5;
  }
  .chart-title {
    font-weight: var(--fw-semibold);
    font-size: var(--fs-sm);
  }
  .chart-wrap {
    position: relative;
    width: 100%;
  }
  svg {
    display: block;
    overflow: visible;
  }
  svg:focus-visible {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: 4px;
    border-radius: var(--radius-sm);
  }
  .axis text,
  .label {
    fill: var(--text-soft);
    font-size: 12px;
    font-family: var(--font-sans);
    font-variant-numeric: tabular-nums;
  }
  .axis .sup {
    font-size: 9px;
  }
  .dlabel {
    fill: var(--text);
    font-size: 12px;
    font-weight: 600;
    font-family: var(--font-sans);
  }
  .gridline {
    stroke: var(--grid);
    stroke-width: 1;
  }
  .limit {
    stroke: var(--chart-limit);
    stroke-width: 1;
  }
  .line {
    fill: none;
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
  }
  .cutline {
    stroke-dasharray: 4 4;
  }
  .s1 {
    stroke: var(--series-1);
  }
  .s2 {
    stroke: var(--series-2);
  }
  .f1 {
    fill: var(--series-1);
  }
  .f2 {
    fill: var(--series-2);
  }
  .dot {
    stroke: var(--chart-surface);
    stroke-width: 2;
  }
  .cross-mark {
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
  }
  .crosshair {
    stroke: var(--text-soft);
    stroke-width: 1;
  }
  .hit {
    fill: transparent;
  }
  .legend {
    display: flex;
    gap: var(--space-4);
    flex-wrap: wrap;
    font-size: var(--fs-sm);
    padding: 0;
    margin: 0;
    list-style: none;
  }
  .legend li {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .key {
    display: inline-block;
    width: 14px;
    height: 2px;
    border-radius: 1px;
    flex: none;
  }
  .k1 {
    background: var(--series-1);
  }
  .k2 {
    background: var(--series-2);
  }
  .tooltip {
    position: absolute;
    pointer-events: none;
    background: var(--surface-raised);
    box-shadow: var(--shadow-lift);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-3);
    font-size: var(--fs-sm);
    min-width: 11rem;
    z-index: 1;
  }
  .t-head {
    color: var(--text-soft);
    font-size: var(--fs-xs);
  }
  .t-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    white-space: nowrap;
  }
  .t-row b {
    font-variant-numeric: tabular-nums;
  }
</style>
